import { cloneDeep } from 'lodash';
import React, { Component } from 'react';
import { snakeCase } from '../../base/common/utils';

import AuctionConfigService from './auction-config-service';

import { getClass } from '../../../utils/component-helpers';
import AlgorithmDistributions from './algorithm-distributions';
import AuctionDistributionOverride from './auction-distribution-override';
import Modal from '../../base/modals/base-modal';
import PublisherDropdown from './publisher-dropdown';

import './auction-config.scss';

class AuctionConfig extends Component {
	constructor(props) {
		super(props);

		this.service = AuctionConfigService;
		this.defaultPublisherLabel = {full_name: 'Select Publisher'};
		this.state = {
			// Publisher overrides
			publishersWithOverrides: [],
			publisherAlgos: {}, // All publisher override algorithms
			publisherDisplayed: this.defaultPublisherLabel,

			// Algorithms displayed on configuration form
			algorithms: {
				global: null,
				overrides: null,
				new: null,
			},

			// Display messages when updating distributions
			distributionStatus: {
				global: {
					invalid: false,
					saved: false,
				},
				overrides: {
					invalid: false,
					saved: false,
					removed: false,
				},
				new: {
					invalid: false,
					noPublisher: false,
				},
			},

			// New override modal toggle
			overrideOpen: false,
			selectedConfig: 'global',

			// Remove override modal toggle
			removeOverrideOpen: false,
		};

		this.publishers = [];
		this.algorithms = [];
		this.selectedPubId = null;
		this.previousSelectedPubId = null;
	}

	async componentDidMount() {
		this.publishers = await this.service.getPublishers();
		this.algorithms = await this.service.getAuctionDistributions();
		this.populateDistributionData();
	}

	populateDistributionData() {
		const [globalDistributions, pubAlgos] =
			this.service.getDistributionData(this.algorithms);
		this.setState({
			algorithms: {
				global: globalDistributions,
				overrides: globalDistributions,
				new: globalDistributions,
			},
			publisherAlgos: pubAlgos,
		}, () => {
			this.displayPublisherOverrides();
		});
	}

	// type: null|newOverride
	displayPublisherOverrides(type=null) {
		// Update publisher overrides dropdown
		const pubIdsWithOverrides = Object.keys(this.state.publisherAlgos);
		if (pubIdsWithOverrides.length > 0 && type !== 'newOverride') {
			this.selectedPubId = parseInt(pubIdsWithOverrides[0]);
		}
		const publishers = this.publishers.filter((publisher) => {
			return pubIdsWithOverrides.includes(publisher.pub_id.toString());
		});

		this.setState({publishersWithOverrides: publishers});

		if (!this.selectedPubId) return;
		this.displayOverride();
	}

	displayOverride() {
		// Format distributions
		const publisherOverride = cloneDeep(this.state.algorithms.overrides)
			.map((algorithm) => {
				const algorithmName = snakeCase(algorithm.algorithm_name);
				algorithm.distribution_fraction =
					this.state.publisherAlgos[this.selectedPubId][algorithmName];
				algorithm.pub_id = this.selectedPubId;
				return algorithm;
			});

		this.setState((currentState) => ({
			algorithms: {
				...currentState.algorithms,
				overrides: publisherOverride,
			},
			publisherDisplayed: this.service.getPublisherDisplayed(
				this.publishers, this.selectedPubId
			),
		}));
	}

	updateDistribution(value, index, isNewConfig=false) {
		const config = isNewConfig ? 'new' : this.state.selectedConfig;
		const newAlgorithms = cloneDeep(this.state.algorithms[config]);
		newAlgorithms[index].distribution_fraction = value;

		this.setState((currentState) => {
			const state = cloneDeep(currentState);
			state.algorithms[config] = newAlgorithms;
			state.distributionStatus[config] = {
				invalid: false,
				saved: false,
			};
			return state;
		});
	}

	async validateAndSaveDistributions(isNewConfig=false) {
		const config = isNewConfig ? 'new' : this.state.selectedConfig;
		let totalDistribution = 0;
		this.state.algorithms[config].map((algorithms) => {
			return totalDistribution += Number(algorithms.distribution_fraction);
		});

		await this.normalizeDistributions(totalDistribution, config);
		if (!this.state.distributionStatus[config].invalid) {
			this.saveAuctionDistributions(config);
		}
	}

	normalizeDistributions(totalDistribution, config) {
		const newAlgorithms = cloneDeep(this.state.algorithms[config]);
		newAlgorithms.map((algorithm) => {
			// Distributions should have four decimal places
			const normalizedFraction = Number(algorithm.distribution_fraction).toFixed(4);
			// Reset distribution to zero if user tries to submit non-numeric values
			algorithm.distribution_fraction = isNaN(normalizedFraction) ?
				'0.0000' : normalizedFraction;
			return algorithm;
		});

		this.setState((currentState) => {
			const state = cloneDeep(currentState);
			state.algorithms[config] = newAlgorithms;
			state.distributionStatus[config] = {
				invalid: totalDistribution !== 1, // Sum of distributions must equal 1
				saved: false,
			};
			return state;
		});
	}

	saveAuctionDistributions(config) {
		// Format: {algorithmic: '1.000', manual_bid: '0.0000', ...}
		const algorithmDistributions = cloneDeep(this.state.algorithms[config])
			.reduce((result, algorithms) => {
				result[snakeCase(algorithms.algorithm_name)] = algorithms.distribution_fraction;
				return result;
			}, {});

		if (config !== 'global') {
			// Cannot save distribution if no publisher is selected
			if (config === 'new' && !this.selectedPubId) {
				this.setState((currentState) => {
					const state = cloneDeep(currentState);
					state.distributionStatus.new.noPublisher = true;
					return state;
				});
				return;
			}

			this.savePublisherAlgorithms(algorithmDistributions);
			algorithmDistributions.pub_id = this.selectedPubId;

			if (config === 'new') {
				this.setState({overrideOpen: false}, () => {
					this.displayPublisherOverrides('newOverride');
					this.setState((currentState) => {
						const state = cloneDeep(currentState);
						state.distributionStatus.overrides.saved = true;
						return state;
					});
				});
			}
		}

		this.service.saveAuctionDistributions(algorithmDistributions).then(() => {
			this.setState((currentState) => {
				const state = cloneDeep(currentState);
				state.distributionStatus[config] = {
					invalid: false, // Sum of distributions must equal 1
					saved: true,
				};
				return state;
			});
		});
	}

	savePublisherAlgorithms(algorithmDistributions) {
		return this.setState((currentState) => ({
			publisherAlgos: {
				...currentState.publisherAlgos,
				[this.selectedPubId]: algorithmDistributions,
			},
		}));
	}

	createNewOverride() {
		this.toggleModal(true, 'overrideOpen');
		this.previousSelectedPubId = cloneDeep(this.selectedPubId);
		this.selectedPubId = null;

		this.setState((currentState) => {
			const state = cloneDeep(currentState);
			// Show the global distribution on new override page by default
			state.algorithms.new = cloneDeep(this.state.algorithms.global);
			return state;
		});
	}

	resetDisplayMsg() {
		this.setState({
			distributionStatus: {
				global: {
					invalid: false,
					saved: false,
				},
				overrides: {
					invalid: false,
					saved: false,
					removed: false,
				},
				new: {
					invalid: false,
					saved: false,
					noPublisher: false,
				},
			},
		});
	}

	removePublisherOverride() {
		this.service.removePublisherOverride(this.selectedPubId).then(() => {
			this.setState((currentState) => {
				const state = cloneDeep(currentState);
				state.distributionStatus.overrides = {
					invalid: false,
					saved: false,
					removed: true,
				};
				delete state.publisherAlgos[this.selectedPubId];
				state.removeOverrideOpen = false;
				return state;
			}, () => {
				this.selectedPubId = parseInt(Object.keys(this.state.publisherAlgos)[0]);
				// If last publisher is removed, set defaults for overrides view
				if (isNaN(this.selectedPubId)) {
					this.selectedPubId = null;
					this.previousSelectedPubId = null;
					this.setState((currentState) => ({
						algorithms: {
							...currentState.algorithms,
							overrides: this.state.algorithms.global,
						},
						publishersWithOverrides: [],
						publisherDisplayed: this.defaultPublisherLabel,
					}));
				} else {
					this.displayPublisherOverrides();
				}
			});
		});
	}

	toggleModal(opened, modalName, action=null) {
		if (action === 'cancel') this.selectedPubId = this.previousSelectedPubId;

		this.resetDisplayMsg();
		// Display overrides page in the background when adding a new override
		this.setState({
			[modalName]: opened,
			selectedConfig: 'overrides',
		});
	}

	selectConfig(config) {
		this.resetDisplayMsg();
		this.setState({selectedConfig: config});
	}

	selectPublisher(publisher, showOverride=false) {
		this.resetDisplayMsg();
		this.selectedPubId = publisher.pub_id;

		if (showOverride) {
			this.displayOverride();
		}
	}

	render() {
		if (!this.state.algorithms.global) return null;

		const configOverrideModal = this.state.overrideOpen ? (
			<AuctionDistributionOverride
				publishers={this.publishers}
				distributions={this.state.algorithms.new}
				distributionStatus={this.state.distributionStatus.new}
				selectPublisher={this.selectPublisher.bind(this)}
				updateDistribution={this.updateDistribution.bind(this)}
				addOverride={this.validateAndSaveDistributions.bind(this)}
				closeModal={this.toggleModal.bind(this)}
			/>
		) : null;

		const publisherName = this.selectedPubId ?
			this.service.getPublisherDisplayed(
				this.publishers, this.selectedPubId
			).full_name : '';
		const removeOverrideModal = this.state.removeOverrideOpen ? (
			<Modal
				options={{
					identifier: 'remove-override',
					displayMsg: `Are you sure you want to remove overrides for ${publisherName}?`,
					save: {
						txt: 'Yes remove',
						callback: this.removePublisherOverride.bind(this),
					},
					cancel: {
						txt: 'Cancel',
						callback: this.toggleModal.bind(this, false, 'removeOverrideOpen'),
					},
				}}
			/>
		) : null;

		const configs = [
			{
				type: 'global',
				display: 'Global',
				onClick: () => this.selectConfig('global'),
			},
			{
				type: 'overrides',
				display: `${Object.keys(this.state.publisherAlgos).length} Overrides`,
				onClick: () => this.selectConfig('overrides'),
			},
			{
				type: 'new',
				display: '+ New Override',
				onClick: () => this.createNewOverride(),
			},
		];

		const configOptions = configs.map((config) => {
			return (
				<button
					key={config.type}
					className={getClass(`${config.type}-btn`, {
						'selected': this.state.selectedConfig === config.type,
					})}
					onClick={() => config.onClick()}>
					{config.display}
				</button>
			);
		});

		const publisherOverridesDropdown = this.state.selectedConfig === 'overrides' ? (
			<PublisherDropdown
				publishers={this.state.publishersWithOverrides}
				selectPublisher={this.selectPublisher.bind(this)}
				showOverride={true}
				publisherDisplayed={this.state.publisherDisplayed}
			/>
		) : null;

		const currentView = this.state.selectedConfig === 'global' ? 'global' : 'overrides';
		const editDisabled = this.state.selectedConfig === 'overrides' &&
			!this.selectedPubId && !this.previousSelectedPubId;

		const removeOverrideBtn = currentView === 'overrides' ? (
			<button
				className={getClass('remove-btn', {
					'disabled': editDisabled,
				})}
				disabled={editDisabled}
				onClick={() => this.toggleModal(true, 'removeOverrideOpen')}>
				Remove Override
			</button>
		) : null;

		return (
			<>
				<div className="auction-config-container">
					<h3>Auction Configurations</h3>
					{configOptions}
					{publisherOverridesDropdown}
					<AlgorithmDistributions
						distributions={this.state.algorithms[currentView]}
						distributionStatus={this.state.distributionStatus[currentView]}
						editDisabled={editDisabled}
						updateDistribution={this.updateDistribution.bind(this)}
						type="main"
					/>
					{removeOverrideBtn}
					{removeOverrideModal}
					<button
						className={getClass('save-btn', {
							'disabled': editDisabled,
							'global': currentView === 'global',
							'overrides': currentView === 'overrides',
						})}
						disabled={editDisabled}
						onClick={() => this.validateAndSaveDistributions()}>
						Save
					</button>
				</div>
				{configOverrideModal}
			</>
		);
	}
}

export default AuctionConfig;
